import React, { useEffect, useState } from 'react';
import { api, useLanguage } from '../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Target } from 'lucide-react';

const RARITY_BORDER = {
  legendary: 'border-yellow-400/60',
  epic: 'border-purple-400/60',
  rare: 'border-blue-400/60',
  uncommon: 'border-emerald-400/50',
  common: 'border-zinc-400/40',
};

const ROTATION_MS = 5500;

const formatRemainingCopy = (b, language) => {
  const remaining = Math.max(0, b.target - b.current);
  const fr = language === 'fr';
  switch (b.unit) {
    case 'steps_today':
    case 'steps':
    case 'steps_sunday':
    case 'steps_morning':
    case 'steps_night':
      return fr
        ? `Il te reste ${remaining.toLocaleString('fr-CA')} pas`
        : `${remaining.toLocaleString('en-CA')} steps to go`;
    case 'lifetime_steps':
      return fr
        ? `${remaining.toLocaleString('fr-CA')} pas cumules restants`
        : `${remaining.toLocaleString('en-CA')} cumulative steps left`;
    case 'streak_days':
    case 'triple_goal_streak':
      return fr
        ? `${remaining} jour${remaining > 1 ? 's' : ''} d'affilee restant${remaining > 1 ? 's' : ''}`
        : `${remaining} day${remaining > 1 ? 's' : ''} in a row left`;
    case 'goal_days_total':
      return fr
        ? `${remaining} journee${remaining > 1 ? 's' : ''} objectif restante${remaining > 1 ? 's' : ''}`
        : `${remaining} goal day${remaining > 1 ? 's' : ''} left`;
    case 'active_days':
      return fr
        ? `${remaining} jour${remaining > 1 ? 's' : ''} actif${remaining > 1 ? 's' : ''} a venir`
        : `${remaining} active day${remaining > 1 ? 's' : ''} to go`;
    case 'days_since_signup':
      return fr
        ? `${remaining} jour${remaining > 1 ? 's' : ''} depuis ton inscription`
        : `${remaining} day${remaining > 1 ? 's' : ''} since you joined`;
    case 'workouts_4w':
      return fr
        ? `${remaining} entrainement${remaining > 1 ? 's' : ''} restant${remaining > 1 ? 's' : ''} sur 4 semaines`
        : `${remaining} workout${remaining > 1 ? 's' : ''} left in the 4-week block`;
    default:
      return fr ? 'Encore un effort' : 'Almost there';
  }
};

export default function NextBadgeWidget() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const onWalkingPage = location.pathname === '/walking';
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/badges/next?limit=3');
        if (alive) setBadges(r.data?.next_badges || []);
      } catch {
        if (alive) setBadges([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (paused || badges.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % badges.length);
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, [badges.length, paused]);

  if (loading || badges.length === 0) return null;

  const badge = badges[index];
  const border = RARITY_BORDER[badge.rarity] || RARITY_BORDER.common;
  const pct = Math.max(0, Math.min(100, badge.pct || 0));
  const remainingCopy = formatRemainingCopy(badge, language);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mb-4 p-4 bg-gradient-to-br from-zinc-900/80 to-zinc-950/80 backdrop-blur border ${border} rounded-xl transition-all hover:bg-zinc-900`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      data-testid="next-badge-widget"
    >
      <button
        type="button"
        onClick={() => { if (!onWalkingPage) navigate('/walking'); }}
        className="w-full text-left"
        data-testid="next-badge-click"
      >
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-3.5 h-3.5 text-primary" />
          <span className="text-[10px] font-heading uppercase tracking-widest text-primary">
            {language === 'fr' ? 'Prochain Badge' : 'Next Badge'}
          </span>
          <span className="ml-auto text-[10px] font-mono text-muted-foreground">
            {pct.toFixed(0)}%
          </span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={badge.id}
            initial={{ opacity: 0, x: 14 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -14 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="flex items-center gap-3"
          >
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-2xl flex-shrink-0 border-2"
              style={{ borderColor: badge.color, boxShadow: `0 0 14px ${badge.color}33` }}
              data-testid="next-badge-icon"
            >
              {badge.icon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-foreground truncate" data-testid="next-badge-name">
                {badge.name}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">
                {remainingCopy}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-3 h-1.5 w-full bg-zinc-800 rounded-full overflow-hidden">
          <motion.div
            key={`bar-${badge.id}`}
            className="h-full rounded-full"
            style={{ background: `linear-gradient(90deg, ${badge.color}, ${badge.color}cc)` }}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            data-testid="next-badge-progress-bar"
          />
        </div>
      </button>

      {badges.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-3" data-testid="next-badge-dots">
          {badges.map((b, i) => (
            <button
              key={b.id}
              type="button"
              onClick={(e) => { e.stopPropagation(); setIndex(i); }}
              aria-label={`Badge ${i + 1}`}
              data-testid={`next-badge-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-5 bg-primary' : 'w-1.5 bg-zinc-600 hover:bg-zinc-500'
              }`}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}
